import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders(): Record<string, string> {
  try {
    const token = (JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state ?? {})?.token ?? '';
    return token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' };
  } catch {
    return { 'Content-Type': 'application/json' };
  }
}

function fmtUGX(n: number): string {
  return `UGX ${Math.round(n).toLocaleString('en-UG')}`;
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleString('en-UG', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Interfaces ────────────────────────────────────────────────────────────────

interface Location {
  id: string;
  name: string;
}

interface DeliveryZone {
  id: string;
  name: string;
  description: string | null;
  feeUgx: number;
  estimatedMins: number | null;
  isActive: boolean;
}

interface Rider {
  id: string;
  fullName: string;
  phone: string;
  vehicleType: string | null;
  vehiclePlate: string | null;
  isActive: boolean;
}

interface DeliveryOrder {
  id: string;
  orderId: string;
  zoneId: string | null;
  riderId: string | null;
  status: 'pending' | 'assigned' | 'picked_up' | 'delivered' | 'failed';
  customerAddress: string | null;
  customerPhone: string | null;
  deliveryFeeUgx: number;
  deliveredAt: string | null;
  createdAt: string;
  order?: { orderNumber: string; totalUgx: number } | null;
  rider?: { fullName: string; phone: string } | null;
  zone?: { name: string } | null;
}

interface PerformanceReport {
  period: { from: string; to: string };
  summary: {
    total: number;
    delivered: number;
    failed: number;
    pending: number;
    successRate: string;
    avgDeliveryMins: number | null;
  };
  riders: Array<{
    riderId: string;
    fullName: string;
    total: number;
    delivered: number;
    failed: number;
    avgMins: number | null;
  }>;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function statusBadgeClass(status: DeliveryOrder['status']): string {
  const map: Record<DeliveryOrder['status'], string> = {
    pending: 'badge badge-amber',
    assigned: 'badge badge-blue',
    picked_up: 'badge badge-amber',
    delivered: 'badge badge-green',
    failed: 'badge badge-red',
  };
  return map[status];
}

function statusLabel(status: DeliveryOrder['status']): string {
  const map: Record<DeliveryOrder['status'], string> = {
    pending: 'Pending',
    assigned: 'Assigned',
    picked_up: 'Picked Up',
    delivered: 'Delivered',
    failed: 'Failed',
  };
  return map[status];
}

// ─── Zone Modal ────────────────────────────────────────────────────────────────

function ZoneModal({
  initial,
  locationId,
  onClose,
  onSave,
  isSaving,
}: {
  initial?: Partial<DeliveryZone>;
  locationId: string;
  onClose: () => void;
  onSave: (data: object) => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [feeUgx, setFeeUgx] = useState(initial?.feeUgx ?? 0);
  const [estimatedMins, setEstimatedMins] = useState<string>(
    initial?.estimatedMins != null ? String(initial.estimatedMins) : '',
  );
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  const isEdit = Boolean(initial?.id);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h2 className="modal-title">{isEdit ? 'Edit Zone' : 'Add Delivery Zone'}</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Zone Name *</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Kampala Central" />
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Description</label>
            <input className="input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" />
          </div>
          <div className="field">
            <label className="field-label">Delivery Fee (UGX) *</label>
            <input className="input" type="number" min={0} value={feeUgx} onChange={e => setFeeUgx(parseInt(e.target.value) || 0)} />
          </div>
          <div className="field">
            <label className="field-label">Est. Minutes</label>
            <input className="input" type="number" min={0} value={estimatedMins} onChange={e => setEstimatedMins(e.target.value)} placeholder="e.g. 30" />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              id="zone-active"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
            />
            <label htmlFor="zone-active" style={{ fontSize: 13, color: 'var(--text-2)', cursor: 'pointer' }}>Active</label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!name.trim() || isSaving}
            onClick={() => {
              if (!name.trim()) return;
              const payload: Record<string, unknown> = { name: name.trim(), feeUgx, isActive };
              if (!isEdit) payload['locationId'] = locationId;
              if (description.trim()) payload['description'] = description.trim();
              if (estimatedMins !== '') payload['estimatedMins'] = parseInt(estimatedMins);
              onSave(payload);
            }}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Rider Modal ───────────────────────────────────────────────────────────────

function RiderModal({
  initial,
  locationId,
  onClose,
  onSave,
  isSaving,
}: {
  initial?: Partial<Rider>;
  locationId: string;
  onClose: () => void;
  onSave: (data: object) => void;
  isSaving: boolean;
}) {
  const [fullName, setFullName] = useState(initial?.fullName ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [vehicleType, setVehicleType] = useState(initial?.vehicleType ?? '');
  const [vehiclePlate, setVehiclePlate] = useState(initial?.vehiclePlate ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  const isEdit = Boolean(initial?.id);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h2 className="modal-title">{isEdit ? 'Edit Rider' : 'Add Rider'}</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Full Name *</label>
            <input className="input" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="e.g. Moses Kato" />
          </div>
          <div className="field">
            <label className="field-label">Phone *</label>
            <input className="input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+256 7XX XXX XXX" />
          </div>
          <div className="field">
            <label className="field-label">Vehicle Type</label>
            <select className="input" value={vehicleType} onChange={e => setVehicleType(e.target.value)}>
              <option value="">— Select —</option>
              <option value="motorcycle">Motorcycle</option>
              <option value="bicycle">Bicycle</option>
              <option value="car">Car</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Vehicle Plate</label>
            <input className="input" value={vehiclePlate} onChange={e => setVehiclePlate(e.target.value)} placeholder="e.g. UAL 123B" />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              id="rider-active"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
            />
            <label htmlFor="rider-active" style={{ fontSize: 13, color: 'var(--text-2)', cursor: 'pointer' }}>Active</label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!fullName.trim() || !phone.trim() || isSaving}
            onClick={() => {
              if (!fullName.trim() || !phone.trim()) return;
              const payload: Record<string, unknown> = { fullName: fullName.trim(), phone: phone.trim(), isActive };
              if (!isEdit) payload['locationId'] = locationId;
              if (vehicleType) payload['vehicleType'] = vehicleType;
              if (vehiclePlate.trim()) payload['vehiclePlate'] = vehiclePlate.trim();
              onSave(payload);
            }}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Assign Rider Modal ────────────────────────────────────────────────────────

function AssignRiderModal({
  delivery,
  riders,
  onClose,
  onAssign,
  isSaving,
}: {
  delivery: DeliveryOrder;
  riders: Rider[];
  onClose: () => void;
  onAssign: (riderId: string, estimatedMins?: number) => void;
  isSaving: boolean;
}) {
  const activeRiders = riders.filter(r => r.isActive);
  const [riderId, setRiderId] = useState(delivery.riderId ?? '');
  const [estimatedMins, setEstimatedMins] = useState('');

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 380 }}>
        <div className="modal-header">
          <h2 className="modal-title">Assign Rider</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, color: 'var(--text-3)' }}>
            Order <span style={{ fontWeight: 500, color: 'var(--text-2)' }}>#{delivery.order?.orderNumber ?? delivery.orderId.slice(-6)}</span>
          </p>
          <div className="field">
            <label className="field-label">Rider *</label>
            <select className="input" value={riderId} onChange={e => setRiderId(e.target.value)}>
              <option value="">— Select rider —</option>
              {activeRiders.map(r => (
                <option key={r.id} value={r.id}>
                  {r.fullName} · {r.phone}{r.vehicleType ? ` (${r.vehicleType})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Est. Delivery (minutes)</label>
            <input
              className="input"
              type="number"
              min={0}
              value={estimatedMins}
              onChange={e => setEstimatedMins(e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!riderId || isSaving}
            onClick={() => {
              if (!riderId) return;
              onAssign(riderId, estimatedMins !== '' ? parseInt(estimatedMins) : undefined);
            }}
          >
            {isSaving ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export function DeliveryPage() {
  const qc = useQueryClient();

  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const lastDay = today.toISOString().slice(0, 10);

  const [activeTab, setActiveTab] = useState<'orders' | 'setup' | 'performance'>('orders');
  const [locationId, setLocationId] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | DeliveryOrder['status']>('');
  const [zoneModal, setZoneModal] = useState<{ open: boolean; editing?: DeliveryZone }>({ open: false });
  const [riderModal, setRiderModal] = useState<{ open: boolean; editing?: Rider }>({ open: false });
  const [assignModal, setAssignModal] = useState<DeliveryOrder | null>(null);
  const [perfFrom, setPerfFrom] = useState(firstDay);
  const [perfTo, setPerfTo] = useState(lastDay);

  // ── Locations ──────────────────────────────────────────────────────────────

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ['locations'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/locations`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
  });

  useEffect(() => {
    if (locations.length > 0 && !locationId) {
      setLocationId(locations[0]!.id);
    }
  }, [locations, locationId]);

  // ── Delivery Zones ─────────────────────────────────────────────────────────

  const { data: zones = [], isLoading: zonesLoading } = useQuery<DeliveryZone[]>({
    queryKey: ['delivery-zones', locationId],
    queryFn: async () => {
      if (!locationId) return [];
      const res = await fetch(`${API_BASE}/v1/delivery/zones?locationId=${locationId}`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
    enabled: Boolean(locationId),
  });

  const saveZone = useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: object }) => {
      const url = id ? `${API_BASE}/v1/delivery/zones/${id}` : `${API_BASE}/v1/delivery/zones`;
      const res = await fetch(url, { method: id ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(data) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Save failed');
      return j.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['delivery-zones', locationId] });
      setZoneModal({ open: false });
    },
  });

  // ── Riders ─────────────────────────────────────────────────────────────────

  const { data: riders = [], isLoading: ridersLoading } = useQuery<Rider[]>({
    queryKey: ['delivery-riders', locationId],
    queryFn: async () => {
      if (!locationId) return [];
      const res = await fetch(`${API_BASE}/v1/delivery/riders?locationId=${locationId}`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
    enabled: Boolean(locationId),
  });

  const saveRider = useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: object }) => {
      const url = id ? `${API_BASE}/v1/delivery/riders/${id}` : `${API_BASE}/v1/delivery/riders`;
      const res = await fetch(url, { method: id ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(data) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Save failed');
      return j.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['delivery-riders', locationId] });
      setRiderModal({ open: false });
    },
  });

  // ── Delivery Orders ────────────────────────────────────────────────────────

  const { data: deliveries = [], isLoading: deliveriesLoading } = useQuery<DeliveryOrder[]>({
    queryKey: ['deliveries', locationId, statusFilter],
    queryFn: async () => {
      if (!locationId) return [];
      let url = `${API_BASE}/v1/delivery?locationId=${locationId}&limit=50`;
      if (statusFilter) url += `&status=${statusFilter}`;
      const res = await fetch(url, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
    enabled: Boolean(locationId),
    refetchInterval: 30_000,
  });

  const assignRider = useMutation({
    mutationFn: async ({ id, riderId, estimatedMins }: { id: string; riderId: string; estimatedMins?: number }) => {
      const body: Record<string, unknown> = { riderId };
      if (estimatedMins !== undefined) body['estimatedMins'] = estimatedMins;
      const res = await fetch(`${API_BASE}/v1/delivery/${id}/assign`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed to assign rider');
      return j.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deliveries', locationId, statusFilter] });
      setAssignModal(null);
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const res = await fetch(`${API_BASE}/v1/delivery/${id}/status`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ status }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed to update status');
      return j.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deliveries', locationId, statusFilter] });
    },
  });

  // ── Performance Report ─────────────────────────────────────────────────────

  const { data: performance, isLoading: perfLoading } = useQuery<PerformanceReport>({
    queryKey: ['delivery-performance', locationId, perfFrom, perfTo],
    queryFn: async () => {
      if (!locationId) throw new Error('No location');
      const res = await fetch(
        `${API_BASE}/v1/delivery/reports/performance?locationId=${locationId}&from=${perfFrom}&to=${perfTo}`,
        { headers: authHeaders() },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed to load report');
      return j.data;
    },
    enabled: Boolean(locationId) && activeTab === 'performance',
  });

  // ─── Render ──────────────────────────────────────────────────────────────────

  const ORDER_STATUS_FILTERS: { value: '' | DeliveryOrder['status']; label: string }[] = [
    { value: '', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'picked_up', label: 'Picked Up' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'failed', label: 'Failed' },
  ];

  const pendingCount = deliveries.filter(d => d.status === 'pending').length;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>
            {deliveries.length} order{deliveries.length !== 1 ? 's' : ''}{pendingCount > 0 ? ` · ${pendingCount} pending` : ''}
          </p>
        </div>
        {locations.length > 1 && (
          <select className="input" style={{ width: 200 }} value={locationId} onChange={e => setLocationId(e.target.value)}>
            {locations.map(l => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 20 }}>
        {([
          { key: 'orders', label: 'Active Orders' },
          { key: 'setup', label: 'Setup' },
          { key: 'performance', label: 'Performance' },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '6px 16px',
              borderRadius: 7,
              border: 'none',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              background: activeTab === tab.key ? 'var(--surface)' : 'transparent',
              color: activeTab === tab.key ? 'var(--text)' : 'var(--text-3)',
              boxShadow: activeTab === tab.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Active Orders Tab ── */}
      {activeTab === 'orders' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Status filter chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ORDER_STATUS_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 500,
                  border: '1px solid',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  background: statusFilter === f.value ? 'var(--accent)' : 'var(--surface)',
                  borderColor: statusFilter === f.value ? 'var(--accent)' : 'var(--border)',
                  color: statusFilter === f.value ? '#fff' : 'var(--text-2)',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {deliveriesLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} style={{ height: 112, background: 'var(--surface2)', borderRadius: 12, animation: 'pulse 1.5s infinite' }} />
              ))}
            </div>
          ) : deliveries.length === 0 ? (
            <div className="card" style={{ padding: 48, textAlign: 'center' }}>
              <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No delivery orders found.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
              {deliveries.map(d => (
                <div key={d.id} className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* Order header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div>
                      <p style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>
                        #{d.order?.orderNumber ?? d.orderId.slice(-8)}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{fmtDate(d.createdAt)}</p>
                    </div>
                    <span className={statusBadgeClass(d.status)}>{statusLabel(d.status)}</span>
                  </div>

                  {/* Order details */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, color: 'var(--text-2)' }}>
                    {d.order?.totalUgx != null && (
                      <p>
                        <span style={{ color: 'var(--text-3)' }}>Total:</span>{' '}
                        <span style={{ fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(d.order.totalUgx)}</span>
                        {' · '}
                        <span style={{ color: 'var(--text-3)' }}>Fee:</span>{' '}
                        <span style={{ fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{fmtUGX(d.deliveryFeeUgx)}</span>
                      </p>
                    )}
                    {d.customerPhone && (
                      <p><span style={{ color: 'var(--text-3)' }}>Phone:</span> {d.customerPhone}</p>
                    )}
                    {d.customerAddress && (
                      <p style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ color: 'var(--text-3)' }}>Address:</span> {d.customerAddress}
                      </p>
                    )}
                    {d.zone && (
                      <p><span style={{ color: 'var(--text-3)' }}>Zone:</span> {d.zone.name}</p>
                    )}
                    {d.rider && (
                      <p>
                        <span style={{ color: 'var(--text-3)' }}>Rider:</span>{' '}
                        <span style={{ fontWeight: 500, color: 'var(--text)' }}>{d.rider.fullName}</span>{' '}
                        <span style={{ color: 'var(--text-3)' }}>{d.rider.phone}</span>
                      </p>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                    {d.status === 'pending' && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setAssignModal(d)}
                      >
                        Assign Rider
                      </button>
                    )}
                    {d.status === 'assigned' && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--amber)' }}
                        onClick={() => updateStatus.mutate({ id: d.id, status: 'picked_up' })}
                        disabled={updateStatus.isPending}
                      >
                        Picked Up
                      </button>
                    )}
                    {(d.status === 'assigned' || d.status === 'picked_up') && (
                      <>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--green)' }}
                          onClick={() => updateStatus.mutate({ id: d.id, status: 'delivered' })}
                          disabled={updateStatus.isPending}
                        >
                          Delivered
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => updateStatus.mutate({ id: d.id, status: 'failed' })}
                          disabled={updateStatus.isPending}
                        >
                          Failed
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Setup Tab ── */}
      {activeTab === 'setup' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {/* Delivery Zones */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
              <h2 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Delivery Zones</h2>
              <button className="btn btn-primary btn-sm" onClick={() => setZoneModal({ open: true })}>+ Add Zone</button>
            </div>
            {zonesLoading ? (
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} style={{ height: 48, background: 'var(--surface2)', borderRadius: 6, animation: 'pulse 1.5s infinite' }} />
                ))}
              </div>
            ) : zones.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No zones configured.</p>
              </div>
            ) : (
              <div>
                {zones.map((z, idx) => (
                  <div
                    key={z.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 20px',
                      borderBottom: idx < zones.length - 1 ? '1px solid var(--border)' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: z.isActive ? 'var(--green)' : 'var(--text-3)',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{z.name}</p>
                        <p style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
                          {fmtUGX(z.feeUgx)}{z.estimatedMins != null ? ` · ~${z.estimatedMins} min` : ''}
                        </p>
                      </div>
                    </div>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ color: 'var(--accent)', flexShrink: 0, marginLeft: 10 }}
                      onClick={() => setZoneModal({ open: true, editing: z })}
                    >
                      Edit
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Riders */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
              <h2 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Riders</h2>
              <button className="btn btn-primary btn-sm" onClick={() => setRiderModal({ open: true })}>+ Add Rider</button>
            </div>
            {ridersLoading ? (
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} style={{ height: 48, background: 'var(--surface2)', borderRadius: 6, animation: 'pulse 1.5s infinite' }} />
                ))}
              </div>
            ) : riders.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No riders added yet.</p>
              </div>
            ) : (
              <div>
                {riders.map((r, idx) => (
                  <div
                    key={r.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 20px',
                      borderBottom: idx < riders.length - 1 ? '1px solid var(--border)' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: r.isActive ? 'var(--green)' : 'var(--text-3)',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.fullName}</p>
                        <p style={{ fontSize: 11, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.phone}{r.vehicleType ? ` · ${r.vehicleType}` : ''}{r.vehiclePlate ? ` · ${r.vehiclePlate}` : ''}
                        </p>
                      </div>
                    </div>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ color: 'var(--accent)', flexShrink: 0, marginLeft: 10 }}
                      onClick={() => setRiderModal({ open: true, editing: r })}
                    >
                      Edit
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Performance Tab ── */}
      {activeTab === 'performance' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Date range */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <div className="field">
              <label className="field-label">From</label>
              <input className="input" type="date" value={perfFrom} onChange={e => setPerfFrom(e.target.value)} style={{ width: 160 }} />
            </div>
            <div className="field">
              <label className="field-label">To</label>
              <input className="input" type="date" value={perfTo} onChange={e => setPerfTo(e.target.value)} style={{ width: 160 }} />
            </div>
          </div>

          {perfLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} style={{ height: 96, background: 'var(--surface2)', borderRadius: 12, animation: 'pulse 1.5s infinite' }} />
                ))}
              </div>
              <div style={{ height: 192, background: 'var(--surface2)', borderRadius: 12, animation: 'pulse 1.5s infinite' }} />
            </div>
          ) : !performance ? (
            <div className="card" style={{ padding: 48, textAlign: 'center' }}>
              <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No performance data for this period.</p>
            </div>
          ) : (
            <>
              {/* KPI cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16 }}>
                {[
                  { label: 'Total Deliveries', value: String(performance.summary.total), highlight: false },
                  { label: 'Delivered', value: String(performance.summary.delivered), highlight: false },
                  { label: 'Failed', value: String(performance.summary.failed), highlight: false },
                  { label: 'Success Rate', value: performance.summary.successRate, highlight: true },
                  {
                    label: 'Avg Delivery Time',
                    value: performance.summary.avgDeliveryMins != null
                      ? `${Math.round(performance.summary.avgDeliveryMins)} min`
                      : '—',
                    highlight: false,
                  },
                ].map(kpi => (
                  <div key={kpi.label} className="stat-card">
                    <p className="stat-label">{kpi.label}</p>
                    <p className="stat-value" style={{ color: kpi.highlight ? 'var(--accent)' : undefined }}>
                      {kpi.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Rider table */}
              {performance.riders.length > 0 && (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
                    <h2 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Rider Performance</h2>
                  </div>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Rider</th>
                          <th style={{ textAlign: 'center' }}>Total</th>
                          <th style={{ textAlign: 'center' }}>Delivered</th>
                          <th style={{ textAlign: 'center' }}>Failed</th>
                          <th style={{ textAlign: 'center' }}>Avg Min</th>
                        </tr>
                      </thead>
                      <tbody>
                        {performance.riders.map(r => (
                          <tr key={r.riderId}>
                            <td style={{ fontWeight: 500, color: 'var(--text)' }}>{r.fullName}</td>
                            <td style={{ textAlign: 'center', color: 'var(--text-2)' }}>{r.total}</td>
                            <td style={{ textAlign: 'center', color: 'var(--green)', fontWeight: 500 }}>{r.delivered}</td>
                            <td style={{ textAlign: 'center', color: 'var(--red)', fontWeight: 500 }}>{r.failed}</td>
                            <td style={{ textAlign: 'center', color: 'var(--text-2)' }}>
                              {r.avgMins != null ? `${Math.round(r.avgMins)} min` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Modals ── */}

      {zoneModal.open && locationId && (
        <ZoneModal
          initial={zoneModal.editing}
          locationId={locationId}
          onClose={() => setZoneModal({ open: false })}
          onSave={data => saveZone.mutate({ id: zoneModal.editing?.id, data })}
          isSaving={saveZone.isPending}
        />
      )}

      {riderModal.open && locationId && (
        <RiderModal
          initial={riderModal.editing}
          locationId={locationId}
          onClose={() => setRiderModal({ open: false })}
          onSave={data => saveRider.mutate({ id: riderModal.editing?.id, data })}
          isSaving={saveRider.isPending}
        />
      )}

      {assignModal && (
        <AssignRiderModal
          delivery={assignModal}
          riders={riders}
          onClose={() => setAssignModal(null)}
          onAssign={(riderId, estimatedMins) =>
            assignRider.mutate({ id: assignModal.id, riderId, estimatedMins })
          }
          isSaving={assignRider.isPending}
        />
      )}
    </div>
  );
}
